if (!customElements.get('kalo-home-hero')) {
  // Rotation starts once the page has loaded so the first (LCP) slide never competes with it.
  const afterLoad = (callback) => {
    const idle = () => ('requestIdleCallback' in window ? requestIdleCallback(callback, { timeout: 2000 }) : setTimeout(callback, 200));
    if (document.readyState === 'complete') idle();
    else window.addEventListener('load', idle, { once: true });
  };

  customElements.define('kalo-home-hero', class extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('[data-hero-slide]')];
      this.toggle = this.querySelector('[data-hero-toggle]');
      if (this.slides.length < 2) return;

      afterLoad(() => {
        if (this.isConnected && !this.ready) this.init();
      });
    }

    init() {
      this.ready = true;
      this.index = 0;
      this.timer = null;
      this.images = this.querySelector('[data-hero-images]');
      this.counter = this.querySelector('[data-hero-current]');
      this.progress = this.querySelector('[data-hero-progress]');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.paused = this.motion.matches;
      this.hovered = false;
      this.focused = false;
      this.editorSelected = false;
      this.interval = Number(this.dataset.interval) || 4000;
      this.events = new AbortController();
      const options = { signal: this.events.signal };

      this.slides[0].classList.add('is-active');
      this.images.classList.add('is-ready');

      this.querySelector('[data-hero-prev]').addEventListener('click', () => this.goTo(this.index - 1), options);
      this.querySelector('[data-hero-next]').addEventListener('click', () => this.goTo(this.index + 1), options);
      this.toggle.addEventListener('click', () => {
        this.paused = !this.paused;
        this.update();
      }, options);
      this.addEventListener('pointerenter', (event) => {
        this.hovered = event.pointerType === 'mouse';
        this.update();
      }, options);
      this.addEventListener('pointerleave', () => {
        this.hovered = false;
        this.update();
      }, options);
      this.addEventListener('focusin', () => {
        this.focused = true;
        this.update();
      }, options);
      this.addEventListener('focusout', (event) => {
        this.focused = this.contains(event.relatedTarget);
        this.update();
      }, options);
      document.addEventListener('visibilitychange', () => this.update(), options);
      this.motion.addEventListener('change', () => {
        this.paused = this.motion.matches;
        this.update();
      }, options);
      this.addEventListener('shopify:block:select', (event) => {
        const index = this.slides.indexOf(event.target.closest('[data-hero-slide]'));
        if (index < 0) return;
        this.editorSelected = true;
        this.goTo(index);
      }, options);
      this.addEventListener('shopify:block:deselect', () => {
        this.editorSelected = false;
        this.update();
      }, options);

      this.querySelector('[data-hero-controls]').hidden = false;
      this.update();
    }

    goTo(index) {
      const total = this.slides.length;
      const next = ((index % total) + total) % total;
      if (next !== this.index) {
        this.slides[this.index].classList.remove('is-active');
        this.slides[next].classList.add('is-active');
        this.index = next;
        this.counter.textContent = String(next + 1).padStart(2, '0');
      }
      this.update(true);
    }

    update(restart = false) {
      this.toggle.setAttribute('aria-label', this.paused ? this.toggle.dataset.playLabel : this.toggle.dataset.pauseLabel);
      this.toggle.setAttribute('data-paused', String(this.paused));
      const suspended = this.paused || this.hovered || this.focused || this.editorSelected || document.hidden;

      if (suspended) {
        clearTimeout(this.timer);
        this.timer = null;
        this.setProgress(false);
      } else if (restart || !this.timer) {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => {
          this.timer = null;
          this.goTo(this.index + 1);
        }, this.interval);
        this.setProgress(true);
      }
    }

    // Progress is a single CSS transition per slide instead of per-frame JS updates.
    setProgress(running) {
      const bar = this.progress;
      bar.style.transition = 'none';
      bar.style.setProperty('--hero-progress', 0);
      if (!running) return;
      void bar.offsetWidth;
      bar.style.transition = `transform ${this.interval}ms linear`;
      bar.style.setProperty('--hero-progress', 1);
    }

    disconnectedCallback() {
      this.events?.abort();
      clearTimeout(this.timer);
      this.timer = null;
      this.ready = false;
    }
  });
}
